import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  TrendingUp, RefreshCw, Loader2, DollarSign, ShoppingCart, Truck,
  AlertCircle, CheckCircle2, Copy, Search, PlusCircle,
  Settings, Layers, Eye, Download, X,
  ExternalLink, BarChart3, Zap, Edit3, FileSpreadsheet, Sparkles
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import {
  AdProfitabilityResponse, AdPerformanceRow,
  TikTokAdsConfig
} from '../../types';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import './TikTokAdsProfitReport.css';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_3d', label: 'Last 3 Days' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_14d', label: 'Last 14 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

const PLATFORMS = [
  { value: 'tiktok', label: 'TikTok Ads' },
  { value: 'facebook', label: 'Facebook / Meta' },
  { value: 'all', label: 'All Platforms' },
];

interface TikTokAdsProfitReportProps {
  defaultPlatform?: 'tiktok' | 'facebook' | 'all';
}

export const TikTokAdsProfitReport: React.FC<TikTokAdsProfitReportProps> = ({ defaultPlatform = 'tiktok' }) => {
  const [data, setData] = useState<AdProfitabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Filters
  const [platform, setPlatform] = useState<string>(defaultPlatform);
  const [datePreset, setDatePreset] = useState('last_7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals & Panels
  const [selectedAdForOrders, setSelectedAdForOrders] = useState<AdPerformanceRow | null>(null);
  const [isLogSpendModalOpen, setIsLogSpendModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  
  // Toast notifications
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    if (defaultPlatform) {
      setPlatform(defaultPlatform);
    }
  }, [defaultPlatform]);

  // Log Spend Form State: 'auto' | 'single' | 'bulk'
  const [spendTab, setSpendTab] = useState<'auto' | 'single' | 'bulk'>('auto');

  // Auto-Fetch Form State (for users with Advertiser Account)
  const [autoFetchForm, setAutoFetchForm] = useState({
    platform: defaultPlatform === 'facebook' ? 'facebook' : 'tiktok',
    ad_id: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [fetchingLive, setFetchingLive] = useState(false);
  const [livePreview, setLivePreview] = useState<any>(null);
  const [savingLiveAd, setSavingLiveAd] = useState(false);

  // Single Manual Entry State (for users without API credentials)
  const [singleSpendForm, setSingleSpendForm] = useState({
    platform: defaultPlatform === 'facebook' ? 'facebook' : 'tiktok',
    ad_id: '',
    ad_name: '',
    campaign_name: '',
    date: new Date().toISOString().split('T')[0],
    spend: '',
    impressions: '',
    clicks: '',
    notes: '',
  });

  // Bulk Spend State
  const [bulkPlatform, setBulkPlatform] = useState<'tiktok' | 'facebook'>(defaultPlatform === 'facebook' ? 'facebook' : 'tiktok');
  const [bulkSpendText, setBulkSpendText] = useState('');
  const [savingSpend, setSavingSpend] = useState(false);

  // API Config State (TikTok & Meta)
  const [configTab, setConfigTab] = useState<'tiktok' | 'meta'>(defaultPlatform === 'facebook' ? 'meta' : 'tiktok');
  const [configs, setConfigs] = useState<TikTokAdsConfig[]>([]);
  const [configForm, setConfigForm] = useState<Partial<TikTokAdsConfig>>({
    advertiser_id: '',
    access_token: '',
    currency: 'USD',
    usd_to_bdt_rate: 122.0,
    is_active: true,
    auto_sync: false,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingApi, setTestingApi] = useState(false);

  // Meta API Config State
  const [metaConfigs, setMetaConfigs] = useState<any[]>([]);
  const [metaConfigForm, setMetaConfigForm] = useState({
    id: undefined as number | undefined,
    ad_account_id: '',
    access_token: '',
    app_id: '',
    app_secret: '',
    page_id: '',
    page_name: '',
    name: 'Primary Meta Ad Account',
    is_active: true,
  });
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [userPages, setUserPages] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [savingMetaConfig, setSavingMetaConfig] = useState(false);
  const [testingMetaApi, setTestingMetaApi] = useState(false);
  const [syncingApi, setSyncingApi] = useState(false);

  // Load Analytics Data
  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      const params: any = { platform, date_preset: datePreset };
      if (datePreset === 'custom') {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      if (selectedPageId) {
        params.page_id = selectedPageId;
      }

      const res = await adminAPI.getAdProfitabilityAnalytics(params);
      setData(res.data || res);
    } catch (err: any) {
      console.error('Failed to load profitability analytics:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to load marketing profitability data.');
    } finally {
      setLoading(false);
    }
  }, [platform, datePreset, startDate, endDate, selectedPageId]);

  // Load TikTok Ads Config
  const loadConfigs = useCallback(async () => {
    try {
      const res = await adminAPI.getTikTokAdsConfig();
      const list = res.data || res;
      if (Array.isArray(list) && list.length > 0) {
        setConfigs(list);
        setConfigForm(list[0]);
      }
    } catch (err) {
      console.warn('Unable to load TikTok Ads configs:', err);
    }
  }, []);

  // Load Meta Ads Config
  const loadMetaConfigs = useCallback(async () => {
    try {
      const res = await adminAPI.getMetaAdsConfig();
      const list = res.data?.results || res.data || res;
      if (Array.isArray(list) && list.length > 0) {
        setMetaConfigs(list);
        setMetaConfigForm({
          id: list[0].id,
          ad_account_id: list[0].ad_account_id || '',
          access_token: list[0].access_token || '',
          app_id: list[0].app_id || '',
          app_secret: list[0].app_secret || '',
          page_id: list[0].page_id || '',
          page_name: list[0].page_name || '',
          name: list[0].name || 'Primary Meta Ad Account',
          is_active: list[0].is_active ?? true,
        });
      }
    } catch (err) {
      console.warn('Unable to load Meta Ads configs:', err);
    }
  }, []);

  // Fetch Facebook Pages owned by user
  const handleFetchPages = async () => {
    try {
      setLoadingPages(true);
      const res = await adminAPI.fetchMetaPages({
        access_token: metaConfigForm.access_token || undefined,
        config_id: metaConfigForm.id,
      });
      const data = res.data || res;
      if (data.status === 'success' && Array.isArray(data.pages)) {
        setUserPages(data.pages);
        if (data.pages.length === 0) {
          setToast({ show: true, message: 'No Facebook pages found for this token. You can type Page ID manually.', type: 'error' });
        } else {
          setToast({ show: true, message: `Found ${data.pages.length} Facebook page(s)! Select your page below.`, type: 'success' });
          if (!metaConfigForm.page_id && data.pages.length === 1) {
            setMetaConfigForm(prev => ({ ...prev, page_id: data.pages[0].id, page_name: data.pages[0].name }));
          }
        }
      } else {
        setToast({ show: true, message: data.message || 'Failed to fetch Facebook pages', type: 'error' });
      }
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to fetch Facebook pages', type: 'error' });
    } finally {
      setLoadingPages(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    loadConfigs();
    loadMetaConfigs();
  }, [loadConfigs, loadMetaConfigs]);

  // Copy helper
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setToast({ show: true, message: `Copied ${label}: ${text}`, type: 'success' });
  };

  // Filter Ads rows by search query
  const filteredAds = useMemo(() => {
    if (!data?.ads_breakdown) return [];
    if (!searchQuery.trim()) return data.ads_breakdown;
    const q = searchQuery.toLowerCase().trim();
    return data.ads_breakdown.filter(
      (a) =>
        a.ad_id.toLowerCase().includes(q) ||
        (a.ad_name && a.ad_name.toLowerCase().includes(q)) ||
        (a.campaign_name && a.campaign_name.toLowerCase().includes(q)) ||
        (a.product_name && a.product_name.toLowerCase().includes(q))
    );
  }, [data, searchQuery]);

  // Save Single Ad Spend
  const handleSaveSingleSpend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleSpendForm.ad_id || !singleSpendForm.date) {
      setToast({ show: true, message: 'Please enter Ad ID and Date.', type: 'error' });
      return;
    }
    try {
      setSavingSpend(true);
      await adminAPI.createAdSpendLog({
        platform: singleSpendForm.platform as any,
        ad_id: singleSpendForm.ad_id.trim(),
        ad_name: singleSpendForm.ad_name.trim(),
        campaign_name: singleSpendForm.campaign_name.trim(),
        date: singleSpendForm.date,
        spend: parseFloat(singleSpendForm.spend) || 0,
        impressions: parseInt(singleSpendForm.impressions) || 0,
        clicks: parseInt(singleSpendForm.clicks) || 0,
        notes: singleSpendForm.notes,
      });
      setToast({ show: true, message: 'Daily Ad Spend saved successfully!', type: 'success' });
      setIsLogSpendModalOpen(false);
      setSingleSpendForm({
        platform: 'tiktok',
        ad_id: '',
        ad_name: '',
        campaign_name: '',
        date: new Date().toISOString().split('T')[0],
        spend: '',
        impressions: '',
        clicks: '',
        notes: '',
      });
      loadAnalytics();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to save Ad Spend.', type: 'error' });
    } finally {
      setSavingSpend(false);
    }
  };

  // Bulk Save Spend (from CSV/Spreadsheet Paste)
  const handleBulkSaveSpend = async () => {
    if (!bulkSpendText.trim()) {
      setToast({ show: true, message: 'Please paste CSV or spreadsheet rows.', type: 'error' });
      return;
    }

    try {
      setSavingSpend(true);
      const lines = bulkSpendText.trim().split('\n');
      const entries: any[] = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Skip headers if user pasted header line
        if (
          i === 0 &&
          (line.toLowerCase().includes('date') || line.toLowerCase().includes('ad_id') || line.toLowerCase().includes('spend'))
        ) {
          continue;
        }

        // Support Tab separated (TSV/Excel) or Comma separated (CSV)
        const parts = line.includes('\t') ? line.split('\t') : line.split(',');
        const [rawDate, rawAdId, rawSpend, rawImpr, rawClicks, rawAdName, rawCamp] = parts.map((p) => p?.trim() || '');

        if (!rawAdId || !rawDate) continue;

        entries.push({
          platform: bulkPlatform,
          date: rawDate,
          ad_id: rawAdId,
          spend: parseFloat(rawSpend.replace(/[^0-9.]/g, '')) || 0,
          impressions: parseInt(rawImpr.replace(/[^0-9]/g, '')) || 0,
          clicks: parseInt(rawClicks.replace(/[^0-9]/g, '')) || 0,
          ad_name: rawAdName || '',
          campaign_name: rawCamp || '',
        });
      }

      if (entries.length === 0) {
        setToast({ show: true, message: 'No valid rows detected. Check format: Date, Ad_ID, Spend, Impressions, Clicks', type: 'error' });
        return;
      }

      const res = await adminAPI.bulkSaveAdSpendLogs(entries);
      setToast({
        show: true,
        message: `Successfully saved ${res.data?.saved_count || entries.length} ad spend entries!`,
        type: 'success',
      });
      setIsLogSpendModalOpen(false);
      setBulkSpendText('');
      loadAnalytics();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Bulk save failed.', type: 'error' });
    } finally {
      setSavingSpend(false);
    }
  };

  // Fetch Live Metrics for a Single Ad ID
  const handleFetchLiveAd = async (saveToDb = false) => {
    if (!autoFetchForm.ad_id.trim()) {
      setToast({ show: true, message: 'Please enter Ad ID', type: 'error' });
      return;
    }

    try {
      setFetchingLive(true);
      const res = await adminAPI.fetchLiveAd({
        platform: autoFetchForm.platform,
        ad_id: autoFetchForm.ad_id.trim(),
        date: autoFetchForm.date,
        save_to_db: saveToDb,
      });

      const resData = res.data?.data || res.data;
      setLivePreview(resData);

      if (saveToDb) {
        setToast({
          show: true,
          message: `Live spend and performance for Ad ${autoFetchForm.ad_id} saved to database!`, 
          type: 'success',
        });
        loadAnalytics();
      } else {
        setToast({
          show: true,
          message: 'Live data retrieved directly from Ad Account!',
          type: 'success',
        });
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to fetch live data. Please check Ad ID and API settings.';
      setToast({ show: true, message: msg, type: 'error' });
    } finally {
      setFetchingLive(false);
    }
  };

  // Save Live Previewed Ad to Database
  const handleSaveLivePreview = async () => {
    if (!livePreview) return;
    try {
      setSavingLiveAd(true);
      await adminAPI.createAdSpendLog({
        platform: livePreview.platform,
        ad_id: livePreview.ad_id,
        date: livePreview.date || autoFetchForm.date,
        spend: livePreview.spend,
        impressions: livePreview.impressions,
        clicks: livePreview.clicks,
        ad_name: livePreview.ad_name,
        campaign_name: livePreview.campaign_name,
        is_api_synced: true,
      });
      setToast({
        show: true,
        message: `Live spend for Ad ${livePreview.ad_id} saved to database!`, 
        type: 'success',
      });
      setLivePreview({ ...livePreview, saved_to_db: true });
      loadAnalytics();
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.error || 'Failed to save live spend.',
        type: 'error',
      });
    } finally {
      setSavingLiveAd(false);
    }
  };

  // Save TikTok Ads Config
  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configForm.advertiser_id || !configForm.access_token) {
      setToast({ show: true, message: 'Advertiser ID and Access Token are required.', type: 'error' });
      return;
    }

    try {
      setSavingConfig(true);
      if (configForm.id) {
        await adminAPI.updateTikTokAdsConfig(configForm.id, configForm);
      } else {
        await adminAPI.createTikTokAdsConfig(configForm);
      }
      setToast({ show: true, message: 'TikTok Ads settings saved successfully!', type: 'success' });
      setIsConfigModalOpen(false);
      loadConfigs();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to save config.', type: 'error' });
    } finally {
      setSavingConfig(false);
    }
  };

  // Test TikTok Connection
  const handleTestConnection = async () => {
    if (!configForm.id) {
      setToast({ show: true, message: 'Please save configuration first.', type: 'error' });
      return;
    }
    try {
      setTestingApi(true);
      const res = await adminAPI.testTikTokAdsConnection(configForm.id);
      setToast({
        show: true,
        message: res.data?.message || 'Connection to TikTok Marketing API verified!',
        type: 'success',
      });
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.message || 'Connection test failed. Check Advertiser ID & Token.',
        type: 'error',
      });
    } finally {
      setTestingApi(false);
    }
  };

  // Save Meta Ads Config
  const handleSaveMetaConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!metaConfigForm.ad_account_id || !metaConfigForm.access_token) {
      setToast({ show: true, message: 'Meta Ad Account ID and Access Token are required.', type: 'error' });
      return;
    }
    try {
      setSavingMetaConfig(true);
      if (metaConfigForm.id) {
        await adminAPI.updateMetaAdsConfig(metaConfigForm.id, metaConfigForm);
      } else {
        await adminAPI.createMetaAdsConfig(metaConfigForm);
      }
      setToast({ show: true, message: 'Meta / Facebook Ads settings saved successfully!', type: 'success' });
      setIsConfigModalOpen(false);
      loadMetaConfigs();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to save Meta config.', type: 'error' });
    } finally {
      setSavingMetaConfig(false);
    }
  };

  // Test Meta Connection
  const handleTestMetaConnection = async () => {
    if (!metaConfigForm.id) {
      setToast({ show: true, message: 'Please save Meta configuration first.', type: 'error' });
      return;
    }
    try {
      setTestingMetaApi(true);
      const res = await adminAPI.testMetaAdsConnection(metaConfigForm.id);
      setToast({
        show: true,
        message: res.data?.message || 'Connection to Meta Marketing API verified!',
        type: 'success',
      });
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.message || 'Connection test failed. Check Ad Account ID & Token.',
        type: 'error',
      });
    } finally {
      setTestingMetaApi(false);
    }
  };

  // Unified Sync for TikTok, Meta, or All
  const handleSyncAds = async () => {
    try {
      setSyncingApi(true);
      const res = await adminAPI.syncPlatformAds({
        platform,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });

      const resData = res.data || res;
      let syncMsg = 'Ad accounts synced successfully!';
      if (resData.results) {
        const ttCount = resData.results.tiktok?.synced_count ?? 0;
        const fbCount = resData.results.facebook?.synced_ads ?? 0;
        syncMsg = `Sync complete: TikTok (${ttCount}), Meta (${fbCount}) Ads updated!`;
      }

      setToast({
        show: true,
        message: syncMsg,
        type: 'success',
      });
      loadAnalytics();
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.error || 'Ads Sync failed. Check API credentials.',
        type: 'error',
      });
    } finally {
      setSyncingApi(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!filteredAds.length) return;
    const headers = [
      'Ad ID',
      'Ad Name',
      'Campaign',
      'Spend (BDT)',
      'Impressions',
      'Clicks',
      'CTR%',
      'Total Orders',
      'Delivered Orders',
      'Conversion Rate %',
      'CPA ($)',
      'Delivered Revenue ($)',
      'COGS ($)',
      'Courier Cost ($)',
      'True Net Profit ($)',
      'Margin %',
      'ROAS',
      'POAS',
    ];

    const rows = filteredAds.map((a) => [
      `"${a.ad_id}"`,
      `"${a.ad_name || ''}"`,
      `"${a.campaign_name || ''}"`,
      a.spend.toFixed(2),
      a.impressions,
      a.clicks,
      `${a.ctr.toFixed(2)}%`,
      a.orders_count,
      a.delivered_count,
      `${a.conversion_rate.toFixed(2)}%`,
      a.cpa.toFixed(2),
      a.gross_revenue.toFixed(2),
      a.cogs.toFixed(2),
      a.courier_expense.toFixed(2),
      a.true_net_profit.toFixed(2),
      `${a.margin_percentage.toFixed(1)}%`,
      a.roas.toFixed(2),
      a.poas.toFixed(2),
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${platform}_ad_profitability_${datePreset}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const summary = data?.summary;
  const isNetProfitPositive = (summary?.true_net_profit || 0) >= 0;

  return (
    <div className="admin-page adm-page ad-profit-page">
      {/* Error Alert */}
      {errorMsg && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 18px', marginBottom: 20, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={18} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Unified Single Row Action & Filter Toolbar */}
      <div className="profit-toolbar">
        <div className="profit-toolbar-left">
          {/* Platform Selector */}
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="profit-select">
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          {/* Date Presets */}
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className="profit-select">
            {DATE_PRESETS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>

          {/* Facebook Page Filter */}
          {(platform === 'facebook' || platform === 'all') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <select
                value={selectedPageId}
                onChange={(e) => setSelectedPageId(e.target.value)}
                className="profit-select"
                style={{ fontWeight: 600, color: '#1e293b' }}
                title="Filter campaigns by Facebook Page"
              >
                <option value="">
                  {data?.page_filter?.active_page_name
                    ? `Page: ${data.page_filter.active_page_name}`
                    : '-- All Pages / Active Config --'}
                </option>
                {data?.page_filter?.available_pages?.map((p: any) => (
                  <option key={p.page_id} value={p.page_id}>
                    Page: {p.page_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Date Pickers */}
          {datePreset === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="profit-date-input"
              />
              <span style={{ color: '#94a3b8' }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="profit-date-input"
              />
            </div>
          )}

          <button className="profit-btn profit-btn-outline" onClick={loadAnalytics} disabled={loading} title="Refresh Analytics">
            {loading ? <Loader2 size={14} className="adm-spin" /> : <RefreshCw size={14} />} Refresh
          </button>
        </div>

        <div className="profit-toolbar-right">
          {/* Search Filter */}
          <div className="profit-search-wrapper">
            <Search size={14} className="profit-search-icon" />
            <input
              type="text"
              placeholder="Search Ad ID, Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="profit-search-input"
            />
          </div>

          <button className="profit-btn profit-btn-outline" onClick={handleExportCSV} title="Export Report CSV">
            <Download size={14} /> Export CSV
          </button>

          <button className="profit-btn profit-btn-outline" onClick={() => setIsConfigModalOpen(true)}>
            <Settings size={14} /> API Settings
          </button>

          <button className="profit-btn profit-btn-primary" onClick={() => setIsLogSpendModalOpen(true)}>
            <PlusCircle size={14} /> Log / Fetch Ad Spend
          </button>

          <button className="profit-btn profit-btn-sync" onClick={handleSyncAds} disabled={syncingApi}>
            {syncingApi ? <Loader2 size={14} className="adm-spin" /> : <Zap size={14} />}
            {syncingApi
              ? 'Syncing...'
              : platform === 'facebook'
              ? 'Sync Meta API'
              : platform === 'all'
              ? 'Sync All APIs'
              : 'Sync TikTok API'}
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="profit-kpi-grid">
        {/* HERO: True Net Profit Card */}
        <div className={`profit-kpi-card hero-profit ${!isNetProfitPositive ? 'negative' : ''}`}>
          <div className="profit-kpi-header">
            <div className="profit-kpi-title">True Net Profit</div>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: 5, borderRadius: 7, display: 'flex', alignItems: 'center' }}>
              <TrendingUp size={16} color="#ffffff" />
            </div>
          </div>
          <div className="profit-kpi-value">
            ${summary ? summary.true_net_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>Margin: <strong>{summary ? summary.margin_percentage : 0}%</strong></span>
            <span>POAS: <strong>{summary ? summary.poas : 0}x</strong></span>
          </div>
        </div>

        {/* Total Ad Spend */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div className="profit-kpi-title">Total Ad Spend</div>
            <div style={{ color: '#7c3aed', display: 'flex', alignItems: 'center' }}>
              <DollarSign size={16} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#7c3aed' }}>
            ${summary ? summary.total_spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>CPA: <strong>${summary ? summary.cpa.toFixed(2) : '0.00'}</strong></span>
            <span>ROAS: <strong>{summary ? summary.roas : 0}x</strong></span>
          </div>
        </div>

        {/* Delivered Revenue */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div className="profit-kpi-title">Delivered Revenue</div>
            <div style={{ color: '#0369a1', display: 'flex', alignItems: 'center' }}>
              <ShoppingCart size={16} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#0369a1' }}>
            ${summary ? summary.gross_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>Delivered: <strong>{summary ? summary.total_delivered_orders : 0}</strong></span>
            <span>Orders: <strong>{summary ? summary.total_orders_placed : 0}</strong></span>
          </div>
        </div>

        {/* Product Buying Cost (COGS) */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div className="profit-kpi-title">COGS (Product Cost)</div>
            <div style={{ color: '#d97706', display: 'flex', alignItems: 'center' }}>
              <Layers size={16} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#d97706' }}>
            ${summary ? summary.cogs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>Gross Profit: <strong>${summary ? (summary.gross_revenue - summary.cogs).toFixed(2) : '0.00'}</strong></span>
          </div>
        </div>

        {/* Net Courier & Return Loss */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div className="profit-kpi-title">Courier & Returns</div>
            <div style={{ color: '#dc2626', display: 'flex', alignItems: 'center' }}>
              <Truck size={16} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#dc2626' }}>
            ${summary ? summary.courier_expense.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>Returned: <strong>{summary ? summary.total_cancelled_orders : 0}</strong></span>
          </div>
        </div>
      </div>

      {/* Secondary Stats Bar (Traffic & Funnel KPIs) */}
      <div className="profit-secondary-stats">
        <div className="profit-stat-item">
          <span>Impressions:</span>
          <strong>{summary ? summary.total_impressions.toLocaleString() : 0}</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>Clicks:</span>
          <strong>{summary ? summary.total_clicks.toLocaleString() : 0}</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>Click Rate (CTR%):</span>
          <strong>{summary ? summary.total_ctr.toFixed(2) : 0}%</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>Order Conversion (CR%):</span>
          <strong>{summary ? summary.conversion_rate.toFixed(2) : 0}%</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>Date Range:</span>
          <strong>{data?.date_range ? `${data.date_range.start_date} to ${data.date_range.end_date}` : '...'}</strong>
        </div>
      </div>

      {/* Main Per-Ad Breakdown Table */}
      <div className="profit-table-card">
        <div className="profit-table-header">
          <div className="profit-table-title">
            <BarChart3 size={18} />
            <span>Ad Performance & Net Profit ({filteredAds.length} Ad IDs)</span>
          </div>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            Click "View Orders" on any Ad ID to see order details
          </div>
        </div>

        <div className="profit-table-wrapper">
          <table className="profit-table">
            <thead>
              <tr>
                <th>Ad ID / Name</th>
                <th>Campaign</th>
                <th style={{ textAlign: 'right' }}>Spend</th>
                <th style={{ textAlign: 'center' }}>Impressions / Clicks</th>
                <th style={{ textAlign: 'center' }}>CR% (Conversion)</th>
                <th style={{ textAlign: 'center' }}>Orders (Delivered / Total)</th>
                <th style={{ textAlign: 'right' }}>CPA (Delivery Cost)</th>
                <th style={{ textAlign: 'right' }}>Revenue</th>
                <th style={{ textAlign: 'right' }}>COGS</th>
                <th style={{ textAlign: 'right' }}>Courier Cost</th>
                <th style={{ textAlign: 'right' }}>True Net Profit</th>
                <th style={{ textAlign: 'center' }}>ROAS / POAS</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                    <Loader2 size={24} className="adm-spin" style={{ margin: '0 auto 8px auto' }} />
                    Loading data... Please wait
                  </td>
                </tr>
              )}

              {!loading && filteredAds.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                    No Ad spend or orders found for this time period.
                    <div style={{ marginTop: 12 }}>
                      <button className="profit-btn profit-btn-primary" onClick={() => setIsLogSpendModalOpen(true)}>
                        <PlusCircle size={14} /> Log Ad Spend
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                filteredAds.map((ad) => {
                  const isProfitable = ad.true_net_profit >= 0;
                  return (
                    <tr key={ad.ad_id}>
                      {/* Ad ID and Name */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{ad.ad_name || `Ad ${ad.ad_id}`}</span>
                            {ad.is_api_synced ? (
                              <span
                                className="profit-badge profit-badge-purple"
                                style={{ fontSize: 10, padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                title="Auto-fetched from ad account API"
                              >
                                <Zap size={10} /> Auto API
                              </span>
                            ) : (
                              <span
                                className="profit-badge profit-badge-gray"
                                style={{ fontSize: 10, padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                title="Entered manually or via spreadsheet"
                              >
                                <Edit3 size={10} /> Manual
                              </span>
                            )}
                            {ad.platform && (
                              <span
                                className="profit-badge profit-badge-blue"
                                style={{ fontSize: 10, padding: '2px 6px', textTransform: 'uppercase' }}
                              >
                                {ad.platform === 'facebook' ? 'Meta' : ad.platform}
                              </span>
                            )}
                          </div>
                          <span className="profit-ad-id-badge">
                            ID: {ad.ad_id}
                            <button
                              className="profit-copy-btn"
                              title="Copy Ad ID"
                              onClick={() => handleCopy(ad.ad_id, 'Ad ID')}
                            >
                              <Copy size={12} />
                            </button>
                          </span>
                        </div>
                      </td>

                      {/* Campaign / Product */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: 13, color: '#334155' }}>{ad.campaign_name || '—'}</span>
                          {ad.page_name && (
                            <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 500 }}>
                              Page: {ad.page_name}
                            </span>
                          )}
                          {ad.product_name && (
                            <span style={{ fontSize: 11, color: '#64748b' }}>Product: {ad.product_name}</span>
                          )}
                        </div>
                      </td>

                      {/* Spend */}
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>
                        ${ad.spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* Impressions / Clicks (CTR) */}
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12 }}>
                          {ad.impressions.toLocaleString()} / <strong>{ad.clicks.toLocaleString()}</strong>
                        </div>
                        <span style={{ fontSize: 11, color: '#64748b' }}>CTR: {ad.ctr.toFixed(2)}%</span>
                      </td>

                      {/* Conversion Rate */}
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className={`profit-badge ${ad.conversion_rate > 3 ? 'profit-badge-green' : 'profit-badge-gray'}`}
                        >
                          {ad.conversion_rate.toFixed(2)}%
                        </span>
                      </td>

                      {/* Orders Count */}
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
                          {ad.delivered_count} Delivered
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          Total: {ad.orders_count} (Cancelled: {ad.cancelled_count})
                        </div>
                      </td>

                      {/* CPA */}
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {ad.delivered_count > 0 ? `$${ad.cpa.toFixed(2)}` : '—'}
                      </td>

                      {/* Revenue */}
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                        ${ad.gross_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* COGS */}
                      <td style={{ textAlign: 'right', color: '#d97706' }}>
                        ${ad.cogs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* Courier Cost */}
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>
                        ${ad.courier_expense.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* True Net Profit */}
                      <td style={{ textAlign: 'right' }}>
                        <span
                          className={`profit-badge ${isProfitable ? 'profit-badge-green' : 'profit-badge-red'}`}
                          style={{ fontSize: 13, padding: '5px 10px' }}
                        >
                          {isProfitable ? '+' : ''}${ad.true_net_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          Margin: {ad.margin_percentage.toFixed(1)}%
                        </div>
                      </td>

                      {/* ROAS & POAS */}
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12 }}>
                          ROAS: <strong>{ad.roas}x</strong>
                        </div>
                        <div style={{ fontSize: 11, color: isProfitable ? '#15803d' : '#b91c1c' }}>
                          POAS: <strong>{ad.poas}x</strong>
                        </div>
                      </td>

                      {/* Action */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="profit-btn profit-btn-outline"
                          style={{ height: 30, padding: '0 10px', fontSize: 12 }}
                          onClick={() => setSelectedAdForOrders(ad)}
                        >
                          <Eye size={13} /> Orders ({ad.orders_count})
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attributed Orders Drawer / Modal */}
      {selectedAdForOrders && (
        <div className="profit-modal-overlay" onClick={() => setSelectedAdForOrders(null)}>
          <div className="profit-modal profit-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="profit-modal-header">
              <div>
                <h3 className="profit-modal-title">
                  Orders for Ad ID: {selectedAdForOrders.ad_id}
                </h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {selectedAdForOrders.ad_name} • Total Orders: {selectedAdForOrders.orders_count}
                </span>
              </div>
              <button className="profit-modal-close" onClick={() => setSelectedAdForOrders(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="profit-modal-body">
              {(!selectedAdForOrders.recent_orders || selectedAdForOrders.recent_orders.length === 0) ? (
                <div className="profit-empty-state">
                  <div className="profit-empty-icon">📦</div>
                  <p>No orders found under this Ad ID.</p>
                </div>
              ) : (
                <div className="profit-orders-list">
                  {selectedAdForOrders.recent_orders.map((order) => {
                    const statusClass =
                      order.status === 'delivered'
                        ? 'profit-badge-green'
                        : order.status === 'cancelled'
                        ? 'profit-badge-red'
                        : 'profit-badge-blue';

                    return (
                      <div key={order.id} className="profit-order-card">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <strong style={{ fontSize: 14, color: '#0f172a' }}>{order.order_number}</strong>
                            <span className={`profit-badge ${statusClass}`} style={{ textTransform: 'capitalize' }}>
                              {order.status}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Customer: <strong>{order.customer_name}</strong> ({order.customer_phone})
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            Order Date: {new Date(order.created_at).toLocaleString()}
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0369a1' }}>
                            ${order.final_amount.toFixed(2)}
                          </div>
                          <div style={{ fontSize: 11, color: '#d97706' }}>
                            Product Cost: ${order.total_cost.toFixed(2)}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginTop: 2 }}>
                            Gross Profit: ${order.gross_profit.toFixed(2)}
                          </div>
                          <a
                            href={`/admin/orders?search=${order.order_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 11,
                              color: '#2563eb',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 2,
                              marginTop: 4,
                            }}
                          >
                            View Order <ExternalLink size={10} />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="profit-modal-footer">
              <button className="profit-btn profit-btn-outline" onClick={() => setSelectedAdForOrders(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Ad Spend Modal */}
      {isLogSpendModalOpen && (
        <div className="profit-modal-overlay" onClick={() => setIsLogSpendModalOpen(false)}>
          <div className="profit-modal profit-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="profit-modal-header">
              <div>
                <h3 className="profit-modal-title">Log & Ingest Ad Spend</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Automatic API Fetch (Advertiser Account) or Direct Manual Entry
                </span>
              </div>
              <button className="profit-modal-close" onClick={() => setIsLogSpendModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="profit-modal-body">
              {/* 3 Main Ingestion Tabs */}
              <div className="profit-tabs">
                <button
                  className={`profit-tab-btn ${spendTab === 'auto' ? 'active' : ''}`}
                  onClick={() => setSpendTab('auto')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Zap size={14} style={{ color: spendTab === 'auto' ? '#8b5cf6' : undefined }} />
                  ⚡ Automatic Live API (Auto-Fetch)
                </button>
                <button
                  className={`profit-tab-btn ${spendTab === 'single' ? 'active' : ''}`}
                  onClick={() => setSpendTab('single')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Edit3 size={14} style={{ color: spendTab === 'single' ? '#3b82f6' : undefined }} />
                  ✍️ Single Manual Entry
                </button>
                <button
                  className={`profit-tab-btn ${spendTab === 'bulk' ? 'active' : ''}`}
                  onClick={() => setSpendTab('bulk')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <FileSpreadsheet size={14} style={{ color: spendTab === 'bulk' ? '#10b981' : undefined }} />
                  📋 Excel / CSV Bulk Paste
                </button>
              </div>

              {/* TAB 1: AUTOMATIC LIVE API AUTO-FETCH */}
              {spendTab === 'auto' && (
                <div>
                  <div className="profit-helper-box">
                    <Sparkles size={18} style={{ flexShrink: 0, marginTop: 1, color: '#10b981' }} />
                    <div>
                      <strong>Automatic Live API Fetch:</strong> If you have an Advertiser Account with TikTok or Meta, simply enter the Ad ID and date to fetch real-time spend, impressions, and clicks directly from the official API.
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Advertising Platform</label>
                      <select
                        className="profit-form-input"
                        value={autoFetchForm.platform}
                        onChange={(e) => {
                          setAutoFetchForm({ ...autoFetchForm, platform: e.target.value as any });
                          setLivePreview(null);
                        }}
                      >
                        <option value="tiktok">TikTok Ads Manager</option>
                        <option value="facebook">Meta / Facebook Ads Manager</option>
                      </select>
                    </div>

                    <div className="profit-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <label className="profit-form-label" style={{ margin: 0 }}>Date</label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            type="button"
                            style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4 }}
                            onClick={() => setAutoFetchForm({ ...autoFetchForm, date: new Date().toISOString().split('T')[0] })}
                          >
                            Today
                          </button>
                          <button
                            type="button"
                            style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4 }}
                            onClick={() => {
                              const y = new Date();
                              y.setDate(y.getDate() - 1);
                              setAutoFetchForm({ ...autoFetchForm, date: y.toISOString().split('T')[0] });
                            }}
                          >
                            Yesterday
                          </button>
                        </div>
                      </div>
                      <input
                        type="date"
                        required
                        className="profit-form-input"
                        value={autoFetchForm.date}
                        onChange={(e) => setAutoFetchForm({ ...autoFetchForm, date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Ad ID *</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 181234567890123 or Meta Ad ID"
                        className="profit-form-input"
                        value={autoFetchForm.ad_id}
                        onChange={(e) => setAutoFetchForm({ ...autoFetchForm, ad_id: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleFetchLiveAd(false);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="profit-btn profit-btn-primary"
                        onClick={() => handleFetchLiveAd(false)}
                        disabled={fetchingLive || !autoFetchForm.ad_id.trim()}
                        style={{ whiteSpace: 'nowrap', minWidth: 150 }}
                      >
                        {fetchingLive ? <Loader2 size={15} className="adm-spin" /> : <Search size={15} />}
                        {fetchingLive ? 'Fetching...' : '🔍 Fetch Live'}
                      </button>
                    </div>
                    <div className="profit-form-help">
                      Copy and paste the Ad ID from your advertising account's Ads Manager
                    </div>
                  </div>

                  {/* Live Preview Card */}
                  {livePreview && (
                    <div className="profit-live-card">
                      <div className="profit-live-header">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="profit-live-title">{livePreview.ad_name || `Ad ${livePreview.ad_id}`}</span>
                            <span className="profit-badge profit-badge-green" style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }}></span> Live API Data
                            </span>
                          </div>
                          <span style={{ fontSize: 12, color: '#64748b' }}>
                            {livePreview.campaign_name ? `Campaign: ${livePreview.campaign_name} • ` : ''}
                            Platform: <strong>{livePreview.platform === 'facebook' ? 'Meta' : 'TikTok'}</strong> • Date: {livePreview.date || autoFetchForm.date}
                          </span>
                        </div>

                        <div>
                          {livePreview.saved_to_db ? (
                            <span className="profit-badge profit-badge-green" style={{ fontSize: 12, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <CheckCircle2 size={14} /> Saved to Database
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="profit-btn profit-btn-primary"
                              onClick={handleSaveLivePreview}
                              disabled={savingLiveAd}
                            >
                              {savingLiveAd ? <Loader2 size={14} className="adm-spin" /> : <PlusCircle size={14} />}
                              {savingLiveAd ? 'Saving...' : '💾 Save to Database'}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="profit-live-grid">
                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">Spend</div>
                          <div className="profit-live-stat-value" style={{ color: '#7c3aed' }}>
                            ${Number(livePreview.spend || livePreview.spend_original || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Ad Spend (USD)</div>
                        </div>

                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">Impressions (Views)</div>
                          <div className="profit-live-stat-value">
                            {Number(livePreview.impressions || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Ad Views</div>
                        </div>

                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">Clicks</div>
                          <div className="profit-live-stat-value" style={{ color: '#0369a1' }}>
                            {Number(livePreview.clicks || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Website Visits</div>
                        </div>

                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">CTR / CPC</div>
                          <div className="profit-live-stat-value" style={{ fontSize: 14 }}>
                            {Number(livePreview.ctr || 0).toFixed(2)}% / ${Number(livePreview.cpc || 0).toFixed(2)}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Performance Rate</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 1-Click Sync Entire Account Box */}
                  <div className="profit-sync-box">
                    <div>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                        ⚡ Sync Entire Ad Account?
                      </div>
                      <div className="profit-sync-box-text">
                        Instead of fetching ads one by one, automatically sync and update today's ad spend for all ads in your {autoFetchForm.platform === 'facebook' ? 'Meta' : 'TikTok'} ad account with a single click.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="profit-btn profit-btn-sync"
                      onClick={handleSyncAds}
                      disabled={syncingApi}
                    >
                      {syncingApi ? <Loader2 size={14} className="adm-spin" /> : <RefreshCw size={14} />}
                      {syncingApi ? 'Syncing...' : `Sync Entire ${autoFetchForm.platform === 'facebook' ? 'Meta' : 'TikTok'} Account`}
                    </button>
                  </div>

                  {/* Notice for merchants without API */}
                  <div className="profit-helper-box warning" style={{ marginTop: 20 }}>
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 13 }}>
                      <strong>No Ad Account or API Access?</strong> No problem! If you boost through an agency or don't have API access, switch to the <strong>[✍️ Single Manual Entry]</strong> or <strong>[📋 Excel / CSV Bulk Paste]</strong> tab above to log your spend directly. Net profit and loss will be accurately calculated!
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: SINGLE MANUAL ENTRY */}
              {spendTab === 'single' && (
                <form onSubmit={handleSaveSingleSpend}>
                  <div className="profit-helper-box info">
                    <Edit3 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <strong>Manual Spend Entry:</strong> If you don't have direct API credentials, you can manually log the Ad ID and daily spend provided by your marketing agency.
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Platform *</label>
                      <select
                        className="profit-form-input"
                        value={singleSpendForm.platform}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, platform: e.target.value })}
                      >
                        <option value="tiktok">TikTok Ads</option>
                        <option value="facebook">Facebook / Meta Ads</option>
                        <option value="google">Google Ads</option>
                        <option value="other">Other Marketing</option>
                      </select>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">Date *</label>
                      <input
                        type="date"
                        required
                        className="profit-form-input"
                        value={singleSpendForm.date}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Ad ID *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 181234567890123 or agency code"
                        className="profit-form-input"
                        value={singleSpendForm.ad_id}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, ad_id: e.target.value })}
                      />
                      <div className="profit-form-help">This Ad ID will automatically match orders to calculate profitability</div>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">Ad Name / Creative</label>
                      <input
                        type="text"
                        placeholder="e.g. Leather Bag Video 01"
                        className="profit-form-input"
                        value={singleSpendForm.ad_name}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, ad_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Total Ad Spend (Spend $) *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        placeholder="0.00"
                        className="profit-form-input"
                        value={singleSpendForm.spend}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, spend: e.target.value })}
                      />
                      <div className="profit-form-help">Enter total ad spend in Dollars ($)</div>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">Campaign Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Winter Sale 2026"
                        className="profit-form-input"
                        value={singleSpendForm.campaign_name}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, campaign_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Impressions (Optional)</label>
                      <input
                        type="number"
                        placeholder="0"
                        className="profit-form-input"
                        value={singleSpendForm.impressions}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, impressions: e.target.value })}
                      />
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">Clicks (Optional)</label>
                      <input
                        type="number"
                        placeholder="0"
                        className="profit-form-input"
                        value={singleSpendForm.clicks}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, clicks: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Notes (Remarks / Agency Note)</label>
                    <textarea
                      rows={2}
                      placeholder="Additional notes or agency billing reference..."
                      className="profit-form-textarea"
                      value={singleSpendForm.notes}
                      onChange={(e) => setSingleSpendForm({ ...singleSpendForm, notes: e.target.value })}
                    />
                  </div>

                  <div className="profit-modal-footer" style={{ margin: '0 -24px -24px -24px' }}>
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsLogSpendModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="profit-btn profit-btn-primary" disabled={savingSpend}>
                      {savingSpend ? 'Saving...' : 'Save Manual Spend'}
                    </button>
                  </div>
                </form>
              )}

              {/* TAB 3: BULK SPREADSHEET PASTE */}
              {spendTab === 'bulk' && (
                <div>
                  <div className="profit-helper-box info">
                    <FileSpreadsheet size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <strong>Bulk Paste from Excel / Google Sheets:</strong> Copy and paste daily spend logs from your agency. Format:
                      <br />
                      <code>Date &emsp; Ad_ID &emsp; Spend &emsp; Impressions &emsp; Clicks &emsp; Ad_Name</code>
                    </div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Platform</label>
                    <select
                      className="profit-form-input"
                      value={bulkPlatform}
                      onChange={(e) => setBulkPlatform(e.target.value as any)}
                    >
                      <option value="tiktok">TikTok Ads</option>
                      <option value="facebook">Meta / Facebook Ads</option>
                    </select>
                  </div>

                  <textarea
                    rows={8}
                    placeholder={`2026-09-30\t181234567890123\t1500.00\t12000\t240\tHandbag Ad 1\n2026-09-30\t181234567890124\t2000.00\t18000\t310\tWatch Video 2`}
                    className="profit-form-textarea"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                    value={bulkSpendText}
                    onChange={(e) => setBulkSpendText(e.target.value)}
                  />

                  <div className="profit-modal-footer" style={{ margin: '16px -24px -24px -24px' }}>
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsLogSpendModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="profit-btn profit-btn-primary"
                      onClick={handleBulkSaveSpend}
                      disabled={savingSpend}
                    >
                      {savingSpend ? 'Uploading...' : 'Save Bulk Spend'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Marketing APIs Configuration Modal */}
      {isConfigModalOpen && (
        <div className="profit-modal-overlay" onClick={() => setIsConfigModalOpen(false)}>
          <div className="profit-modal profit-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="profit-modal-header">
              <div>
                <h3 className="profit-modal-title">Marketing APIs Configuration</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Manage API credentials for TikTok and Meta (Facebook) advertising accounts
                </span>
              </div>
              <button className="profit-modal-close" onClick={() => setIsConfigModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="profit-modal-body">
              <div className="profit-tabs">
                <button
                  className={`profit-tab-btn ${configTab === 'tiktok' ? 'active' : ''}`}
                  onClick={() => setConfigTab('tiktok')}
                >
                  TikTok Marketing API {configs.length > 0 ? '(Configured ✓)' : ''}
                </button>
                <button
                  className={`profit-tab-btn ${configTab === 'meta' ? 'active' : ''}`}
                  onClick={() => setConfigTab('meta')}
                >
                  Meta (Facebook) Marketing API {metaConfigs.length > 0 ? '(Configured ✓)' : ''}
                </button>
              </div>

              {configTab === 'tiktok' ? (
                <form onSubmit={handleSaveConfig}>
                  <div className="profit-form-group">
                    <label className="profit-form-label">Advertiser ID *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 7123456789012345678"
                      className="profit-form-input"
                      value={configForm.advertiser_id || ''}
                      onChange={(e) => setConfigForm({ ...configForm, advertiser_id: e.target.value })}
                    />
                    <div className="profit-form-help">Found in your TikTok Ads Manager (Advertiser ID)</div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Marketing API Access Token *</label>
                    <textarea
                      rows={3}
                      required
                      placeholder="eyJhbGciOi..."
                      className="profit-form-textarea"
                      value={configForm.access_token || ''}
                      onChange={(e) => setConfigForm({ ...configForm, access_token: e.target.value })}
                    />
                    <div className="profit-form-help">Access token obtained from TikTok Business Center / Developer Portal</div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Ad Currency</label>
                      <select
                        className="profit-form-input"
                        value={configForm.currency || 'USD'}
                        onChange={(e) => setConfigForm({ ...configForm, currency: e.target.value as any })}
                      >
                        <option value="USD">USD ($)</option>
                        <option value="BDT">BDT (৳)</option>
                      </select>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">USD to BDT Exchange Rate</label>
                      <input
                        type="number"
                        step="0.1"
                        className="profit-form-input"
                        value={configForm.usd_to_bdt_rate || 122.0}
                        onChange={(e) => setConfigForm({ ...configForm, usd_to_bdt_rate: parseFloat(e.target.value) || 122.0 })}
                      />
                      <div className="profit-form-help">Converts USD ad spend to BDT at this exchange rate</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                    <input
                      type="checkbox"
                      id="auto_sync"
                      checked={configForm.auto_sync || false}
                      onChange={(e) => setConfigForm({ ...configForm, auto_sync: e.target.checked })}
                    />
                    <label htmlFor="auto_sync" style={{ fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                      Automatically sync ad spend daily in the background (Auto Daily Sync)
                    </label>
                  </div>

                  <div className="profit-modal-footer" style={{ margin: '20px -24px -24px -24px' }}>
                    {configForm.id && (
                      <button
                        type="button"
                        className="profit-btn profit-btn-outline"
                        onClick={handleTestConnection}
                        disabled={testingApi}
                      >
                        {testingApi ? <Loader2 size={13} className="adm-spin" /> : <CheckCircle2 size={13} />}
                        {testingApi ? 'Testing...' : 'Test Connection'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsConfigModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="profit-btn profit-btn-primary" disabled={savingConfig}>
                      {savingConfig ? 'Saving...' : 'Save TikTok Settings'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSaveMetaConfig}>
                  <div className="profit-form-group">
                    <label className="profit-form-label">Meta Ad Account ID *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. act_1234567890123"
                      className="profit-form-input"
                      value={metaConfigForm.ad_account_id}
                      onChange={(e) => setMetaConfigForm({ ...metaConfigForm, ad_account_id: e.target.value })}
                    />
                    <div className="profit-form-help">Ad Account ID from Meta Ads Manager (with or without act_ prefix)</div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Meta System User / Access Token *</label>
                    <textarea
                      rows={3}
                      required
                      placeholder="EAAG..."
                      className="profit-form-textarea"
                      value={metaConfigForm.access_token}
                      onChange={(e) => setMetaConfigForm({ ...metaConfigForm, access_token: e.target.value })}
                    />
                    <div className="profit-form-help">ads_read / ads_management token from Meta Business Manager or Graph API Explorer</div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Meta App ID (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. 9876543210"
                        className="profit-form-input"
                        value={metaConfigForm.app_id}
                        onChange={(e) => setMetaConfigForm({ ...metaConfigForm, app_id: e.target.value })}
                      />
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">Configuration Name</label>
                      <input
                        type="text"
                        className="profit-form-input"
                        value={metaConfigForm.name}
                        onChange={(e) => setMetaConfigForm({ ...metaConfigForm, name: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Facebook Page Configuration */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <label className="profit-form-label" style={{ marginBottom: 0, fontWeight: 600 }}>
                        Facebook Page (Owned by this Store)
                      </label>
                      <button
                        type="button"
                        onClick={handleFetchPages}
                        disabled={loadingPages}
                        className="profit-btn profit-btn-outline"
                        style={{ padding: '3px 8px', fontSize: 11, height: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        {loadingPages ? <Loader2 size={11} className="adm-spin" /> : <RefreshCw size={11} />}
                        Fetch My Pages
                      </button>
                    </div>

                    {userPages.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <select
                          className="profit-form-input"
                          value={metaConfigForm.page_id || ''}
                          onChange={(e) => {
                            const pId = e.target.value;
                            const found = userPages.find(p => p.id === pId);
                            setMetaConfigForm({
                              ...metaConfigForm,
                              page_id: pId,
                              page_name: found ? found.name : ''
                            });
                          }}
                        >
                          <option value="">-- All Pages in Ad Account --</option>
                          {userPages.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} (ID: {p.id})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="profit-form-row" style={{ marginBottom: 0 }}>
                      <div className="profit-form-group" style={{ marginBottom: 0 }}>
                        <input
                          type="text"
                          placeholder="Page ID (e.g. 1048576...)"
                          className="profit-form-input"
                          value={metaConfigForm.page_id || ''}
                          onChange={(e) => setMetaConfigForm({ ...metaConfigForm, page_id: e.target.value })}
                        />
                      </div>
                      <div className="profit-form-group" style={{ marginBottom: 0 }}>
                        <input
                          type="text"
                          placeholder="Page Name (e.g. Pie Store)"
                          className="profit-form-input"
                          value={metaConfigForm.page_name || ''}
                          onChange={(e) => setMetaConfigForm({ ...metaConfigForm, page_name: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="profit-form-help" style={{ marginTop: 6 }}>
                      Select or enter your Facebook Page ID. Campaigns from other pages in this Ad Account will be automatically excluded.
                    </div>
                  </div>

                  <div className="profit-modal-footer" style={{ margin: '20px -24px -24px -24px' }}>
                    {metaConfigForm.id && (
                      <button
                        type="button"
                        className="profit-btn profit-btn-outline"
                        onClick={handleTestMetaConnection}
                        disabled={testingMetaApi}
                      >
                        {testingMetaApi ? <Loader2 size={13} className="adm-spin" /> : <CheckCircle2 size={13} />}
                        {testingMetaApi ? 'Testing...' : 'Test Meta Connection'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsConfigModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="profit-btn profit-btn-primary" disabled={savingMetaConfig}>
                      {savingMetaConfig ? 'Saving...' : 'Save Meta Settings'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default TikTokAdsProfitReport;
